import SocialAccountOption from './social-account-option.component';
import {
  Add as AddIcon,
  AddBusiness as AddBusinessIcon,
  AlternateEmail as AlternateEmailIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Groups as GroupsIcon,
  Memory as MemoryIcon,
  Paid as PaidIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import IOSSwitch from 'src/components/switch/switch.component';
import { Constants } from 'src/constants/stringConstants';
import PaymentDialogComponent from 'src/pages/payment/payment-dialog.component';
import HttpServices from 'src/services/httpService';
import { paymentModuleUrl } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';

const formatCurrency = (value, symbol = '$') => `${symbol}${Number(value || 0).toLocaleString('en-US')}`;
const roundMoney = (value) => Math.round(Number(value || 0) * 100) / 100;

const CustomLifetimePlanComponent = ({ initialRequest = null, isAdmin = false, isPaymentOnly = false, onRequestSaved = () => {} }) => {
  const theme = useTheme();
  const { showToast } = useToaster();
  const { t: translate } = useTranslation();
  const [plan, setPlan] = useState(null);
  const [brandCount, setBrandCount] = useState(1);
  const [userCount, setUserCount] = useState(1);
  const [socialAccountCount, setSocialAccountCount] = useState(0);
  const [planType, setPlanType] = useState(Constants.PlanType.Basic);
  const [isRagEnabled, setIsRagEnabled] = useState(false);
  const [hasCustomDomain, setHasCName] = useState(false);
  const [finalAmount, setFinalAmount] = useState('');
  const [hasManualAmount, setHasManualAmount] = useState(false);
  const [billingEmail, setBillingEmail] = useState('');
  const [savedRequest, setSavedRequest] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [payableAmount, setPayableAmount] = useState(0);
  const [adminRequests, setAdminRequests] = useState([]);
  const [isListLoading, setIsListLoading] = useState(false);
  const billingTimeZone = CommonHelper.GetBrandTimeZone();
  const loggedInUserEmail = String(CommonHelper.GetBrandInfoFromLocalStorage('user_details')?.email || '')
    .trim()
    .toLowerCase();

  const currencySymbol = plan?.currency_symbol || '$';
  const maxQuantity = plan?.max_quantity || 25;
  const isProfessionalPlan = planType === Constants.PlanType.Professional;
  const basicUnitPrice = Number(plan?.basic_unit_price || plan?.brand_unit_price || 0);
  const professionalUnitPrice = Number(plan?.professional_unit_price || basicUnitPrice * 2);
  const selectedBrandUnitPrice = isProfessionalPlan ? professionalUnitPrice : basicUnitPrice;
  const selectedUserUnitPrice = isProfessionalPlan ? Number(plan?.user_unit_price || 0) * 2 : Number(plan?.user_unit_price || 0);
  const brandTotal = roundMoney(brandCount * selectedBrandUnitPrice);
  const userTotal = roundMoney(userCount * selectedUserUnitPrice);
  const ragTotal = isProfessionalPlan && isRagEnabled ? roundMoney(brandCount * Number(plan?.rag_unit_price || 0)) : 0;
  const customDomainTotal = isProfessionalPlan && hasCustomDomain ? Number(plan?.custom_domain_price || 0) : 0;
  const socialAccountTotal = roundMoney(socialAccountCount * Number(plan?.social_account_unit_price || 0));
  const defaultTotal = roundMoney(brandTotal + userTotal + ragTotal + customDomainTotal + socialAccountTotal);

  const checkoutRequest = useMemo(
    () => ({
      brand_count: brandCount,
      user_count: userCount,
      social_account_count: socialAccountCount,
      plan_type: planType,
      has_rag: isProfessionalPlan && isRagEnabled,
      has_cname: isProfessionalPlan && hasCustomDomain,
      final_amount: Number(finalAmount || 0),
      timezone: billingTimeZone,
      billing_email: billingEmail.trim(),
    }),
    [billingEmail, billingTimeZone, brandCount, finalAmount, hasCustomDomain, isProfessionalPlan, isRagEnabled, planType, userCount, socialAccountCount]
  );

  useEffect(() => {
    setIsLoading(true);
    HttpServices.Get({
      url: `${paymentModuleUrl.getLifetimePlan}?timezone=${encodeURIComponent(billingTimeZone || '')}`,
      successCallback: (data) => {
        setPlan(data);
        setIsLoading(false);
      },
      failureCallback: (message) => {
        setIsLoading(false);
        showToast(message || translate('billing.custom_lifetime_plan_info.load_error'), 'e');
      },
    });
  }, [billingTimeZone]);

  const applySavedRequest = useCallback((data) => {
    if (!data?.id) {
      return;
    }
    setSavedRequest(data);
    setBillingEmail(data.billing_email || '');
    setBrandCount(data.brand_count || 1);
    setUserCount(data.user_count || 1);
    setSocialAccountCount(data.social_account_count || 0);
    const savedPlanType =
      data.plan_type ||
      (data.has_rag || data.has_cname ? Constants.PlanType.Professional : Constants.PlanType.Basic);
    setPlanType(savedPlanType);
    setIsRagEnabled(savedPlanType === Constants.PlanType.Professional && Boolean(data.has_rag));
    setHasCName(savedPlanType === Constants.PlanType.Professional && Boolean(data.has_cname));
    setFinalAmount(String(data.final_amount || ''));
    setHasManualAmount(true);
  }, []);

  useEffect(() => {
    if (initialRequest?.id) {
      applySavedRequest(initialRequest);
      return;
    }
    HttpServices.Get({
      url: paymentModuleUrl.customLifetimeRequest,
      successCallback: applySavedRequest,
      failureCallback: () => {},
    });
  }, [applySavedRequest, initialRequest]);

  useEffect(() => {
    if (plan && !hasManualAmount) {
      setFinalAmount(defaultTotal.toFixed(2));
    }
  }, [defaultTotal, hasManualAmount, plan]);

  const loadAdminRequests = useCallback(() => {
    if (!isAdmin) {
      return;
    }
    setIsListLoading(true);
    HttpServices.Get({
      url: paymentModuleUrl.customLifetimeRequests,
      successCallback: (data) => {
        setAdminRequests(Array.isArray(data) ? data : []);
        setIsListLoading(false);
      },
      failureCallback: (message) => {
        setIsListLoading(false);
        showToast(message || translate('billing.custom_lifetime_plan_info.load_requests_error'), 'e');
      },
    });
  }, [isAdmin, showToast]);

  useEffect(() => {
    loadAdminRequests();
  }, [loadAdminRequests]);

  const handleAddRequest = () => {
    setSavedRequest(null);
    setBillingEmail('');
    setBrandCount(1);
    setUserCount(1);
    setSocialAccountCount(0);
    setPlanType(Constants.PlanType.Basic);
    setIsRagEnabled(false);
    setHasCName(false);
    setHasManualAmount(false);
  };

  const handlePlanTypeChange = (nextPlanType) => {
    setPlanType(nextPlanType);
    if (nextPlanType === Constants.PlanType.Basic) {
      setIsRagEnabled(false);
      setHasCName(false);
    }
    setHasManualAmount(false);
  };

  const handleEditRequest = (request) => {
    applySavedRequest(request);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteRequest = (requestID) => {
    if (!requestID) {
      return;
    }
    HttpServices.Delete({
      url: `${paymentModuleUrl.customLifetimeRequest}/${requestID}`,
      successCallback: () => {
        if (savedRequest?.id === requestID) {
          handleAddRequest();
        }
        loadAdminRequests();
        showToast('Custom lifetime billing request deleted.', 's');
      },
      failureCallback: (message) => showToast(message || 'Unable to delete custom lifetime request.', 'e'),
    });
  };

  const handleSubmitRequest = () => {
    const normalizedBillingEmail = billingEmail.trim().toLowerCase();
    if (!normalizedBillingEmail) {
      showToast(translate('billing.custom_lifetime_plan_info.billing_email_required'), 'e');
      return;
    }
    if (loggedInUserEmail && normalizedBillingEmail === loggedInUserEmail) {
      showToast(translate('billing.custom_lifetime_plan_info.own_admin_email_error'), 'e');
      return;
    }
    if (Number(finalAmount || 0) <= 0) {
      showToast(translate('billing.custom_lifetime_plan_info.invalid_final_amount'), 'e');
      return;
    }
    setIsSubmitting(true);
    HttpServices.Post({
      url: paymentModuleUrl.customLifetimeRequest,
      body: checkoutRequest,
      successCallback: (data) => {
        setSavedRequest(data);
        onRequestSaved(data);
        if (isAdmin) {
          loadAdminRequests();
        }
        setIsSubmitting(false);
        showToast('Custom lifetime billing request saved.', 's');
      },
      failureCallback: (message) => {
        setIsSubmitting(false);
        showToast(message || translate('billing.custom_lifetime_plan_info.save_error'), 'e');
      },
    });
  };

  const handlePay = () => {
    HttpServices.Post({
      url: paymentModuleUrl.customLifetimePayment,
      body: { timezone: billingTimeZone },
      successCallback: (data) => {
        setClientSecret(data.payment_intent);
        setPayableAmount(Number(data.converted_amount || 0) / 100);
      },
      failureCallback: (message) => showToast(message || translate('billing.custom_lifetime_plan_info.payment_error'), 'e'),
    });
  };

  const handlePaymentDialog = () => {
    setClientSecret('');
  };

  const summaryRows = [
    { label: translate('billing.social_accounts.title', { defaultValue: 'Social media accounts' }), value: socialAccountTotal },
    { label: translate('billing.custom_lifetime_plan_info.brand_count', { count: brandCount }), value: brandTotal },
    { label: translate('billing.custom_lifetime_plan_info.user_count', { count: userCount }), value: userTotal },
  ];
  if (isProfessionalPlan) {
    summaryRows.push(
      { label: translate('billing.custom_lifetime_plan_info.rag_storage'), value: ragTotal },
      { label: translate('billing.custom_lifetime_plan_info.custom_domain'), value: customDomainTotal }
    );
  }

  const optionCardStyles = {
    p: { xs: 2, sm: 3 },
    height: '100%',
    border: `1px solid ${alpha(theme.palette.grey[500], 0.24)}`,
    boxShadow: 'none',
  };

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Box sx={{ pb: 3 }}>
      {isAdmin && (
        <Card
          sx={{
            p: { xs: 2, sm: 2.5 },
            mb: 2,
            boxShadow: 'none',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                {translate('billing.custom_lifetime_plan_info.org_billing_requests')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {translate('billing.custom_lifetime_plan_info.org_billing_requests_description')}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddRequest} sx={{ fontWeight: 800 }}>
                Add
              </Button>
              <Tooltip title="Refresh requests">
                <span>
                  <IconButton onClick={loadAdminRequests} disabled={isListLoading}>
                    {isListLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>

          <Stack spacing={1.25} sx={{ mt: 2 }}>
            {adminRequests.length === 0 && (
              <Card sx={{ p: 2, boxShadow: 'none', bgcolor: alpha(theme.palette.grey[500], 0.06) }}>
                <Typography variant="body2" color="text.secondary">
                  {translate('billing.custom_lifetime_plan_info.no_requests')}
                </Typography>
              </Card>
            )}
            {adminRequests.map((request) => (
              <Card
                key={request.id}
                sx={{
                  p: 1.5,
                  boxShadow: 'none',
                  border: `1px solid ${
                    savedRequest?.id === request.id ? alpha(theme.palette.primary.main, 0.45) : alpha(theme.palette.grey[500], 0.18)
                  }`,
                }}
              >
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography sx={{ fontWeight: 900, wordBreak: 'break-all' }}>{request.billing_email}</Typography>
                      <Chip size="small" label={request.status || 'submitted'} color={request.status === 'paid' ? 'success' : 'default'} />
                      <Chip size="small" label={`Org #${request.org_id}`} variant="outlined" />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {request.plan_type || Constants.PlanType.Basic} - {request.brand_count} brands, {request.user_count} users, {request.social_account_count || 0} social media accounts
                      {request.plan_type === Constants.PlanType.Professional
                        ? `, RAG ${request.has_rag ? 'yes' : 'no'}, custom domain ${request.has_cname ? 'yes' : 'no'}`
                        : ''}{' '}
                      - {formatCurrency(request.final_amount, request.currency_symbol || currencySymbol)}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title={request.status === 'paid' ? 'Paid requests cannot be edited' : 'Edit request'}>
                      <span>
                        <IconButton color="primary" disabled={request.status === 'paid'} onClick={() => handleEditRequest(request)}>
                          <EditIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={request.status === 'paid' ? 'Paid requests cannot be deleted' : 'Delete request'}>
                      <span>
                        <IconButton color="error" disabled={request.status === 'paid'} onClick={() => handleDeleteRequest(request.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Card>
            ))}
          </Stack>
        </Card>
      )}
      <Card
        sx={{
          p: { xs: 2, sm: 3 },
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
          boxShadow: 'none',
          background: `linear-gradient(135deg, ${alpha(theme.palette.warning.lighter, 0.45)} 0%, #fff 44%, ${alpha(
            theme.palette.primary.lighter,
            0.45
          )} 100%)`,
        }}
      >
        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} lg={8}>
            <Stack spacing={3}>
              <Box>
                <Chip label={translate('billing.custom_lifetime_plan_info.manual_final_payment')} color="warning" size="small" sx={{ mb: 1.5, fontWeight: 700 }} />
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  {isPaymentOnly ? 'Custom Lifetime Payment' : 'Custom Lifetime Plan Info'}
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 720 }}>
                  {isPaymentOnly
                    ? 'Review the approved lifetime request for your organization and complete the one-time payment.'
                    : translate('billing.custom_lifetime_plan_info.request_description')}
                </Typography>
              </Box>

              <Grid container spacing={2}>
                {[
                  {
                    value: Constants.PlanType.Basic,
                    title: 'Basic',
                    price: basicUnitPrice,
                    description: 'Live Chat & Conversations, Knowledge Base, and FAQ.',
                  },
                  {
                    value: Constants.PlanType.Professional,
                    title: 'Professional',
                    price: professionalUnitPrice,
                    description: 'All Basic features plus Ticketing',
                  },
                ].map((option) => {
                  const isSelected = planType === option.value;
                  return (
                    <Grid item xs={12} md={6} key={option.value}>
                      <Card
                        role="button"
                        tabIndex={isPaymentOnly ? -1 : 0}
                        onClick={() => {
                          if (!isPaymentOnly) handlePlanTypeChange(option.value);
                        }}
                        onKeyDown={(event) => {
                          if (!isPaymentOnly && (event.key === 'Enter' || event.key === ' ')) handlePlanTypeChange(option.value);
                        }}
                        sx={{
                          p: 2,
                          cursor: isPaymentOnly ? 'default' : 'pointer',
                          boxShadow: 'none',
                          border: `1px solid ${isSelected ? theme.palette.primary.main : alpha(theme.palette.grey[500], 0.24)}`,
                          bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'background.paper',
                          opacity: isPaymentOnly && !isSelected ? 0.56 : 1,
                        }}
                      >
                        <Stack spacing={0.75}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                            <Typography sx={{ fontWeight: 900 }}>{option.title}</Typography>
                            {isSelected && <Chip size="small" color="primary" label="Selected" />}
                          </Stack>
                          <Typography variant="h5" sx={{ fontWeight: 900 }}>
                            {formatCurrency(option.price, currencySymbol)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            per brand and per user
                          </Typography>
                          <Typography variant="body2">{option.description}</Typography>
                        </Stack>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card sx={optionCardStyles}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <AddBusinessIcon color="primary" />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                          Brands
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatCurrency(selectedBrandUnitPrice, currencySymbol)} per brand
                        </Typography>
                      </Box>
                    </Stack>
                    <Slider
                      value={brandCount}
                      min={1}
                      max={maxQuantity}
                      step={1}
                      valueLabelDisplay="auto"
                      disabled={isPaymentOnly}
                      onChange={(event, value) => setBrandCount(value)}
                      sx={{ mt: 3 }}
                    />
                    <Typography sx={{ mt: 1, fontWeight: 800 }}>{brandCount} selected</Typography>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card sx={optionCardStyles}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <GroupsIcon color="primary" />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                          Users
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatCurrency(selectedUserUnitPrice, currencySymbol)} per user
                        </Typography>
                      </Box>
                    </Stack>
                    <Slider
                      value={userCount}
                      min={1}
                      max={maxQuantity}
                      step={1}
                      valueLabelDisplay="auto"
                      disabled={isPaymentOnly}
                      onChange={(event, value) => setUserCount(value)}
                      sx={{ mt: 3 }}
                    />
                    <Typography sx={{ mt: 1, fontWeight: 800 }}>{translate('billing.custom_lifetime_plan_info.selected_count', { count: userCount })}</Typography>
                  </Card>
                </Grid>

                <Grid item xs={12}><Card sx={optionCardStyles}><SocialAccountOption
                  count={socialAccountCount} onChange={setSocialAccountCount}
                  price={plan?.social_account_unit_price} currencySymbol={currencySymbol}
                  max={maxQuantity} disabled={isPaymentOnly}
                /></Card></Grid>

              {isProfessionalPlan && (
                  <>
                <Grid item xs={12} md={6}>
                  <Card sx={optionCardStyles}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <MemoryIcon color="primary" />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                          {translate('billing.custom_lifetime_plan_info.rag_storage')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {translate('billing.lifetime_plans.add_rag_storage', {
                            price: formatCurrency(plan?.rag_unit_price, currencySymbol),
                          })}
                        </Typography>
                      </Box>
                      <IOSSwitch disabled={isPaymentOnly} checked={isRagEnabled} onChange={(event) => setIsRagEnabled(event.target.checked)} />
                    </Stack>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card sx={optionCardStyles}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <AlternateEmailIcon color="primary" />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                          Custom domain
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          One-time setup at {formatCurrency(plan?.custom_domain_price, currencySymbol)}.
                        </Typography>
                      </Box>
                      <IOSSwitch disabled={isPaymentOnly} checked={hasCustomDomain} onChange={(event) => setHasCName(event.target.checked)} />
                    </Stack>
                  </Card>
                </Grid>
                  </>
                )}
              </Grid>
            </Stack>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Card
              sx={{
                p: { xs: 2, sm: 3 },
                height: '100%',
                boxShadow: 'none',
                border: `1px solid ${alpha(theme.palette.grey[500], 0.28)}`,
                bgcolor: 'background.paper',
              }}
            >
              <Stack spacing={2.25} sx={{ height: '100%' }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <PaidIcon color="warning" />
                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      {translate('billing.custom_lifetime_plan_info.default_calculated_total')}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 900 }}>
                      {formatCurrency(defaultTotal, currencySymbol)}
                    </Typography>
                  </Box>
                </Stack>

                <Divider />

                <Stack spacing={1.5}>
                  {summaryRows.map((row) => (
                    <Stack key={row.label} direction="row" justifyContent="space-between" spacing={2}>
                      <Typography color="text.secondary">{row.label}</Typography>
                      <Typography sx={{ fontWeight: 800 }}>{formatCurrency(row.value, currencySymbol)}</Typography>
                    </Stack>
                  ))}
                </Stack>

                <Divider />

                <TextField
                  fullWidth
                  type="number"
                  label={translate('billing.custom_lifetime_plan_info.final_payment_amount')}
                  value={finalAmount}
                  onChange={(event) => {
                    setHasManualAmount(true);
                    setFinalAmount(event.target.value);
                  }}
                  inputProps={{ min: 1, step: '0.01' }}
                  disabled={isPaymentOnly}
                  helperText="This is the amount saved for the manual billing request. Coupons are ignored here."
                />

                <TextField
                  fullWidth
                  required
                  type="email"
                  label="Billing email"
                  value={billingEmail}
                  onChange={(event) => setBillingEmail(event.target.value)}
                  disabled={isPaymentOnly}
                  helperText={
                    isPaymentOnly
                      ? translate('billing.custom_lifetime_plan_info.assigned_to_portal_admin')
                      : translate('billing.custom_lifetime_plan_info.active_portal_admin_email')
                  }
                />

                <Stack direction="row" spacing={1} alignItems="center">
                  <CheckCircleIcon color="success" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    {translate('billing.custom_lifetime_plan_info.selected_limits_saved')}
                  </Typography>
                </Stack>

                {savedRequest && (
                  <Card sx={{ p: 1.5, boxShadow: 'none', border: `1px solid ${alpha(theme.palette.success.main, 0.28)}` }}>
                    <Stack spacing={0.5}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CheckCircleIcon color="success" fontSize="small" />
                        <Typography sx={{ fontWeight: 800 }}>{translate('billing.custom_lifetime_plan_info.request_saved')}</Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {savedRequest.billing_email} - {formatCurrency(savedRequest.final_amount, savedRequest.currency_symbol || currencySymbol)}
                      </Typography>
                      {savedRequest.payment_link && (
                        <Typography
                          component="a"
                          href={savedRequest.payment_link}
                          target="_blank"
                          rel="noreferrer"
                          variant="body2"
                          sx={{ color: 'primary.main', fontWeight: 700, wordBreak: 'break-all' }}
                        >
                          {translate('billing.custom_lifetime_plan_info.open_payment_link')}
                        </Typography>
                      )}
                    </Stack>
                  </Card>
                )}

                <Box sx={{ flexGrow: 1 }} />

                {isPaymentOnly ? (
                  <Stack spacing={1}>
                    {savedRequest && !savedRequest.can_pay && (
                      <Typography variant="body2" color="warning.main" sx={{ fontWeight: 700 }}>
                        Switch to this billing organization to complete the payment.
                      </Typography>
                    )}
                    <Button
                      fullWidth
                      size="large"
                      variant="contained"
                      disabled={!savedRequest?.can_pay || savedRequest?.status === 'paid' || Number(savedRequest?.final_amount || finalAmount || 0) <= 0}
                      onClick={handlePay}
                      sx={{ py: 1.35, fontWeight: 800 }}
                    >
                      {savedRequest?.status === 'paid'
                        ? 'Payment completed'
                        : `Pay ${formatCurrency(savedRequest?.final_amount || finalAmount, savedRequest?.currency_symbol || currencySymbol)}`}
                    </Button>
                  </Stack>
                ) : (
                  <Button
                    fullWidth
                    size="large"
                    variant="contained"
                    disabled={isSubmitting || !billingEmail.trim() || Number(finalAmount || 0) <= 0}
                    onClick={handleSubmitRequest}
                    sx={{ py: 1.35, fontWeight: 800 }}
                  >
                    {isSubmitting ? (
                      <CircularProgress size={22} color="inherit" />
                    ) : savedRequest ? (
                      translate('billing.custom_lifetime_plan_info.update_request')
                    ) : (
                      translate('billing.custom_lifetime_plan_info.submit_request')
                    )}
                  </Button>
                )}
              </Stack>
            </Card>
          </Grid>
        </Grid>
      </Card>
      {clientSecret && (
        <PaymentDialogComponent
          clientSecret={clientSecret}
          amount={payableAmount}
          planName="Custom Lifetime Plan"
          currencySymbol={savedRequest?.currency_symbol || currencySymbol}
          handleClose={handlePaymentDialog}
        />
      )}
    </Box>
  );
};

export default CustomLifetimePlanComponent;
